import type { OnlineMenuData, Location, Business, OrderPayload, OrderStatusResponse, OnlineStatus, DeliveryZone, DeliveryZoneFeeItem } from './types';

let apiBase = '';

export function setApiBase(base: string) {
  apiBase = base.replace(/\/$/, '');
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, init);
  if (!res.ok) {
    const text = await res.text();
    // The backend aborts with a JSON body { "message": "..." } carrying a clean,
    // localized reason (e.g. the scheduled-order lead-time rule). Surface that
    // directly so the customer sees it, falling back to the raw envelope.
    let message = `API error ${res.status}: ${text}`;
    try {
      const body = JSON.parse(text);
      if (body && typeof body.message === 'string' && body.message.trim()) {
        message = body.message;
      }
    } catch {
      // non-JSON error body — keep the raw envelope
    }
    // Attach the HTTP status and raw body so callers (and Sentry) can tell a
    // 400 validation reject from a 5xx/network failure without re-parsing.
    const err = new Error(message) as ApiError;
    err.status = res.status;
    err.responseBody = text;
    err.path = path;
    throw err;
  }
  return res.json();
}

export interface ApiError extends Error {
  status?: number;
  responseBody?: string;
  path?: string;
}

export function fetchOnlineMenu(): Promise<OnlineMenuData> {
  return fetchJson('/api/menu/online');
}

export function fetchLocations(): Promise<Location[]> {
  return fetchJson('/api/location/online');
}

export function fetchBusiness(): Promise<Business[]> {
  return fetchJson('/api/business/');
}

export function submitOrder(payload: OrderPayload) {
  return fetchJson<{ uuid: string; id: number }>('/api/delivery/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function fetchOrderStatus(uuid: string): Promise<OrderStatusResponse> {
  return fetchJson(`/api/delivery/status/${uuid}`);
}

export function fetchWidgetConfig(): Promise<{ default_location_id: number | null; stripe_publishable_key: string; google_maps_key: string; payment_capture_on_approval?: boolean; widget_theme?: string; min_delivery_time?: number | null; show_min_delivery_time?: boolean; delayed_min_lead_minutes?: number; widget_hero_image?: string | null; default_language?: string; widget_languages?: string[] }> {
  return fetchJson('/api/business/widget-config');
}

export function fetchStripeConfig(): Promise<{ publishable_key: string }> {
  return fetchJson('/api/payment/config');
}

export function createPaymentIntent(amountCents: number): Promise<{ client_secret: string; payment_intent_id: string }> {
  return fetchJson('/api/payment/create-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount: amountCents, currency: 'eur' }),
  });
}

export function fetchOnlineStatus(): Promise<OnlineStatus> {
  return fetchJson('/api/business/online-status');
}

export function fetchDeliveryZones(): Promise<DeliveryZone[]> {
  return fetchJson('/api/delivery_zone/online');
}

export interface ReservationPayload {
  customer_name: string;
  contact_phone: string;
  email?: string;
  reservation_date: string;
  reservation_time: string;
  guests: number;
  note?: string;
}

export function submitReservation(payload: ReservationPayload) {
  return fetchJson<{ uuid: string; id: number }>('/api/reservation/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function fetchReservationConfig(): Promise<{ enabled: boolean; schedule: Array<{ day: number; open: string; close: string; active: boolean }> }> {
  return fetchJson('/api/reservation/online-config');
}

export function fetchReservationStatus(uuid: string): Promise<{ uuid: string; status: 'pending' | 'approved' | 'rejected'; rejected_reason: string | null }> {
  return fetchJson(`/api/reservation/status/${uuid}`);
}

export type AddressCheckResult = {
  lat: number | null;
  lng: number | null;
  in_zone: boolean;
  zone: {
    id: number;
    name: string;
    minimum_order: number | null;
    delivery_fee_item_id: number | null;
    delivery_fee_item: DeliveryZoneFeeItem | null;
  } | null;
  /** How well the typed address matched: 'exact' | 'approximate' | 'city' | 'none' | 'pinned'. */
  precision: string | null;
  /**
   * The address did not resolve to a specific building, so the coordinates are
   * a town/street centroid and carry no zone. Ask the customer to drop a pin
   * and call checkAddress again with it.
   */
  needs_pin: boolean;
};

export function checkAddress(address: string, pin?: { lat: number; lng: number } | null): Promise<AddressCheckResult> {
  return fetchJson('/api/delivery_zone/check-address', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(pin ? { address, lat: pin.lat, lng: pin.lng } : { address }),
  });
}
